<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { stripInlineBase64Images } from 'dashboard/helper/editorHelper';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import FormSelect from 'v3/components/Form/Select.vue';

const props = defineProps({
  messageSignature: {
    type: String,
    default: '',
  },
  messageSignaturePosition: {
    type: String,
    default: 'suffix',
  },
});

const emit = defineEmits(['updateSignature']);

const { t } = useI18n();
const signature = ref(props.messageSignature ?? '');
const signaturePosition = ref(props.messageSignaturePosition ?? 'suffix');
watch(
  () => props.messageSignature ?? '',
  newValue => {
    signature.value = newValue;
  }
);
watch(
  () => props.messageSignaturePosition ?? 'suffix',
  newValue => {
    signaturePosition.value = newValue;
  }
);

const positionOptions = computed(() => [
  {
    value: 'suffix',
    label: t(
      'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.OPTIONS.SUFFIX'
    ),
  },
  {
    value: 'prefix',
    label: t(
      'PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.OPTIONS.PREFIX'
    ),
  },
]);

const updateSignature = () => {
  const { sanitizedContent, hasInlineImages } = stripInlineBase64Images(
    signature.value || ''
  );
  signature.value = sanitizedContent.trim();
  if (hasInlineImages) {
    useAlert(
      t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.INLINE_IMAGE_WARNING')
    );
  }
  emit('updateSignature', signature.value, signaturePosition.value);
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="updateSignature()">
    <WootMessageEditor
      id="message-signature-input"
      v-model="signature"
      class="message-editor h-[10rem] !px-3"
      is-format-mode
      :placeholder="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE.PLACEHOLDER')"
      channel-type="Context::MessageSignature"
      :enable-suggestions="false"
    />
    <FormSelect
      id="message-signature-position"
      v-model="signaturePosition"
      name="messageSignaturePosition"
      spacing="compact"
      class="w-full"
      :options="positionOptions"
      :label="
        $t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.POSITION.TITLE')
      "
    >
      <option
        v-for="option in positionOptions"
        :key="option.value"
        :value="option.value"
        :selected="option.value === signaturePosition"
      >
        {{ option.label }}
      </option>
    </FormSelect>
    <div>
      <NextButton
        type="submit"
        :label="$t('PROFILE_SETTINGS.FORM.MESSAGE_SIGNATURE_SECTION.BTN_TEXT')"
      />
    </div>
  </form>
</template>
